// dashboardData.js

import axios from "api/axios";
import { filterTrackedEntitiesByCreatedAt, filterTrackedEntitiesByYear, getAttributeValue, getYearFromDateValue } from "./utils";
import { get } from "react-hook-form";

// ==================== Constants ====================
const MONTHS = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const YEARS = (() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2018; year <= currentYear; year++) {
    years.push(year.toString());
  }
  return years;
})();

const PERIOD_META = {
  monthly: {
    value: "monthly",
    label: "Monthly",
    badgeLabel: "Monthly View",
    changeLabel: "vs last month",
  },
  quarterly: {
    value: "quarterly",
    label: "Quarterly",
    badgeLabel: "Quarterly View",
    changeLabel: "vs last quarter",
  },
  yearly: {
    value: "yearly",
    label: "Yearly",
    badgeLabel: "Yearly View",
    changeLabel: "vs last year",
  },
};

const SECTOR_COLORS = {
  Education: "hsl(215, 60%, 22%)",
  Infrastructure: "hsl(174, 60%, 40%)",
  Health: "hsl(38, 92%, 50%)",
  "Water and Sanitation": "hsl(280, 50%, 50%)",
  Agriculture: "hsl(152, 60%, 40%)",
  Governance: "hsl(12, 76%, 58%)",
  Commerce: "hsl(215, 15%, 50%)",
};

const PROJECT_BLUEPRINTS = [
  { prefix: "Classroom Block", sector: "Education" },
  { prefix: "Health Post Upgrade", sector: "Health" },
  { prefix: "Drainage Works", sector: "Infrastructure" },
  { prefix: "Borehole Extension", sector: "Water and Sanitation" },
  { prefix: "Market Renovation", sector: "Commerce" },
  { prefix: "Irrigation Support", sector: "Agriculture" },
];

// DHIS2 Program IDs
const PROGRAMS = {
  annualActionPlan: 'ArLnAxhykoz',
  project: 'g3wMUKEMmH3',
  meeting: 'Ch38jUWJpUR',
  igf: 'dYNmYGtArrK',
};

const ATTRIBUTES = {
  year: 'pWkYqcKukAY',
  projectType: 'wtf3BR2YO8w',
  meetingDate: 'Ub0V9Z06aBc',
  igfAmount: 'nlZbT98zSio',
  igfBudgeted: 'xSAif899MME',
  igfSource: 'jcFWiKH0Kud',
  projectDate: 'gkqU35LKO4t',
  projectEndDate: 'qkHXURztKyc',
};

const IGF_AMOUNT_COLLECTED_NAMES = [
  "Amount Collected",
  "Amt Collected",
  "DPAT | Amount Collected",
];

const IGF_AMOUNT_BUDGETED_NAMES = [
  "Amount Budgeted",
  "Amt Budgeted",
  "DPAT | Amount Budgeted",
];

function parseIgfAmount(value) {
  if (value == null || value === "" || value === "N/A") {
    return 0;
  }

  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function getIgfAttributeWithFallback(instance, displayNames, attributeId) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, instance);
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matched = instance?.attributes?.find((attr) => attr.attribute === attributeId);
  const value = matched?.value;
  return value == null || value === "" ? "N/A" : value;
}

function getIgfCollectedFromInstance(instance) {
  return parseIgfAmount(
    getIgfAttributeWithFallback(
      instance,
      IGF_AMOUNT_COLLECTED_NAMES,
      ATTRIBUTES.igfAmount,
    ),
  );
}

function getIgfBudgetedFromInstance(instance) {
  return parseIgfAmount(
    getIgfAttributeWithFallback(
      instance,
      IGF_AMOUNT_BUDGETED_NAMES,
      ATTRIBUTES.igfBudgeted,
    ),
  );
}

function sumIgfCollectedFromInstances(instances = []) {
  return instances.reduce((sum, instance) => {
    if (instance.amount != null && instance.amount !== "N/A") {
      return sum + parseIgfAmount(instance.amount);
    }
    return sum + getIgfCollectedFromInstance(instance);
  }, 0);
}

export function formatIgfCurrency(amount) {
  const value = parseIgfAmount(amount);
  return `GHS ${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const PROJECT_TYPES = {
  project: 'Project',
  programme: 'Programme',
};

const REGIONS = [
  { displayName: "Ahafo Region", id: "sQ7uY7OfGh9" },
  // { displayName: "AO Region", id: "BNvzSyfV6JK" },
  { displayName: "Ashanti Region", id: "dTJUQ6DTyI0" },
  { displayName: "Bono East Region", id: "L0G4TsktrO3" },
  { displayName: "Bono Region", id: "YwYDxOSrbRH" },
  { displayName: "Central Region", id: "c1LjZP7AHwc" },
  { displayName: "Eastern Region", id: "vNmQM26Brq4" },
  { displayName: "Greater Accra Region", id: "t7nEwtHs1Mv" },
  { displayName: "North East Region", id: "p7KW7Z2wpPh" },
  { displayName: "Northern Region", id: "uI6RzC2vT8n" },
  { displayName: "Oti Region", id: "CS9Dp4yJAMs" },
  { displayName: "Savannah Region", id: "uiZovYkacoA" },
  { displayName: "Upper East Region", id: "GIllXny74Ht" },
  { displayName: "Upper West Region", id: "GgfzSoi2pBE" },
  { displayName: "Volta Region", id: "OHNc6Xq1u0y" },
  { displayName: "Western North Region", id: "oIaZqOuUAnn" },
  { displayName: "Western Region", id: "RbzoqLb9jtt" }
];

// ==================== Helper Functions ====================
function hashString(value) {
  return String(value || "").split("").reduce((hash, character) => {
    return character.charCodeAt(0) + ((hash << 5) - hash);
  }, 0) >>> 0;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function round(value, decimals = 1) {
  return Number(value.toFixed(decimals));
}

function average(values) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function formatMillions(value, decimals = 1) {
  return `GHS ${round(value, decimals)}M`;
}

function formatPercent(value, decimals = 1) {
  return `${round(value, decimals)}%`;
}

function formatInteger(value) {
  return Math.round(value).toLocaleString();
}

function formatCount(value) {
  return formatInteger(value);
}

function getPeriodMeta(period) {
  return PERIOD_META[period] || PERIOD_META.monthly;
}

function formatData(data) {
  const districts = [];
  const storedDistricts = localStorage.getItem("districts");
  if (storedDistricts) {
    const parsedDistricts = JSON.parse(storedDistricts);
    districts.push(...parsedDistricts);
  }

  // Create a map of district ID to region name
  const districtToRegionMap = new Map(districts.map(d => [d.value.id, d.value.region]));

  // Create a map of region ID to region name (for direct region entries)
  const regionIdToNameMap = new Map(REGIONS.map(r => [r.id, r.displayName]));

  // Create a map of region name to region ID
  const regionNameToIdMap = new Map(REGIONS.map(r => [r.displayName, r.id]));

  // Calculate totals by region (start with all regions at 0)
  const regionTotalsMap = new Map();
  REGIONS.forEach(region => {
    regionTotalsMap.set(region.displayName, 0);
  });

  Object.entries(data || []).forEach(([orgUnitId, aapArray]) => {
    let regionName = null;

    // First, check if this ID is a district (maps to a region)
    if (districtToRegionMap.has(orgUnitId)) {
      regionName = districtToRegionMap.get(orgUnitId);
    }
    // If not a district, check if it's directly a region ID
    else if (regionIdToNameMap.has(orgUnitId)) {
      regionName = regionIdToNameMap.get(orgUnitId);
    }

    // Add the count to the region
    if (regionName && regionTotalsMap.has(regionName)) {
      regionTotalsMap.set(regionName, regionTotalsMap.get(regionName) + aapArray.length);
    }
  });

  // Transform to the desired format
  const datagroupbyregion = Array.from(regionTotalsMap.entries()).map(([regionName, total]) => ({
    region: regionName,
    id: regionNameToIdMap.get(regionName),
    total: total
  }));

  return datagroupbyregion;
}

function formatRegionalData(data) {
  if (!data) return [];

  // Get districts from localStorage
  const storedDistricts = localStorage.getItem("districts");
  let districts = [];
  if (storedDistricts) {
    const parsedDistricts = JSON.parse(storedDistricts);
    districts.push(...parsedDistricts);
  }

  // Create a map of district ID to district name
  const districtNameMap = new Map();
  districts.forEach(district => {
    districtNameMap.set(district.value.id, district.label);
  });

  // Process the data and aggregate by district
  const datagroupbydistrict = Object.entries(data)
    .map(([orgUnitId, instances]) => {
      const districtName = districtNameMap.get(orgUnitId);
      // Only include if this orgUnit is a district (has a name in the map)
      if (districtName) {
        return {
          district: districtName,
          id: orgUnitId,
          total: instances?.length || 0
        };
      }
      return null;
    })
    .filter(item => item !== null && item.total > 0)
    .sort((a, b) => b.total - a.total);

  return datagroupbydistrict;
}

function formatRegionalIGFData(data) {
  if (!data) return [];

  // Get districts from localStorage
  const storedDistricts = localStorage.getItem("districts");
  let districts = [];
  if (storedDistricts) {
    const parsedDistricts = JSON.parse(storedDistricts);
    districts.push(...parsedDistricts);
  }

  // Create a map of district ID to district name
  const districtNameMap = new Map();
  districts.forEach(district => {
    districtNameMap.set(district.value.id, district.label);
  });

  // Process the data and aggregate by district
  const datagroupbydistrict = Object.entries(data)
    .map(([orgUnitId, instances]) => {
      const districtName = districtNameMap.get(orgUnitId);
      if (districtName) {
        let totalAmount = 0;
        instances.forEach(instance => {
          if (instance.amount != null && instance.amount !== "N/A") {
            totalAmount += parseIgfAmount(instance.amount);
          }
        });
        return {
          district: districtName,
          id: orgUnitId,
          totalAmount: totalAmount,
          totalAmountMillions: round(totalAmount / 1000000, 2)
        };
      }
      return null;
    })
    .filter(item => item !== null && item.totalAmount > 0)
    .sort((a, b) => b.totalAmount - a.totalAmount);

  return datagroupbydistrict;
}

function scaleChange(change, period) {
  const factor = {
    monthly: 1,
    quarterly: 0.84,
    yearly: 1.12,
  }[period] || 1;
  return round(change * factor, 1);
}

function statusFromBudget(planned, executed) {
  const executionRate = planned > 0 ? (executed / planned) * 100 : 0;
  if ((executionRate >= 70) && (executionRate <= 100)) return "Good";
  if ((executionRate >= 50) && (executionRate < 70)) return "On Track";
  if ((executionRate >= 30) && (executionRate < 50)) return "At Risk";
  return "Low";
}

function planExecutionPercentage(planned, executed) {
  return planned > 0 ? (executed / planned) * 100 : 0;
}

function buildProjectBreakdown(totalProjects, budgetUtilization, igfRate, meetings, seed) {
  const completedRate = clamp(
    0.12 + budgetUtilization / 360 + igfRate / 900 + meetings / 300,
    0.24,
    0.58,
  );
  const completedProjects = clamp(
    Math.round(totalProjects * completedRate),
    1,
    Math.max(totalProjects - 1, 1),
  );
  const rolloverRate = clamp(
    0.08 +
    Math.max(0, 70 - budgetUtilization) / 340 +
    Math.max(0, 52 - igfRate) / 420 +
    ((seed >> 5) % 4) / 100,
    0.05,
    0.24,
  );
  const rolloverProjects = clamp(
    Math.round(totalProjects * rolloverRate),
    0,
    Math.max(totalProjects - completedProjects, 0),
  );

  return {
    completedProjects,
    rolloverProjects,
  };
}

function buildActivityPlanBreakdown(totalProjects, completedProjects, meetings, budgetUtilization, seed) {
  const activityPlans = clamp(
    Math.round(totalProjects * (0.9 + ((seed >> 7) % 21) / 100)),
    Math.max(totalProjects - 3, 1),
    Math.max(totalProjects + 14, 1),
  );
  const executionRate = clamp(
    0.44 +
    completedProjects / Math.max(totalProjects, 1) * 0.34 +
    meetings / 48 -
    Math.max(0, 68 - budgetUtilization) / 260,
    0.4,
    0.94,
  );
  const plansExecuted = clamp(Math.round(activityPlans * executionRate), 0, activityPlans);

  return {
    activityPlans,
    plansExecuted,
  };
}

function buildTrendData(seed, period, projectsBase, budgetBase, igfBase) {
  const periodMeta = getPeriodMeta(period);
  const trendConfig = {
    monthly: {
      labels: MONTHS,
      factors: [0.82, 0.88, 0.94, 1.0, 0.97, 1.05],
      projectDrift: 2,
      budgetDrift: 0.6,
      igfDrift: 0.25,
    },
    quarterly: {
      labels: QUARTERS,
      factors: [0.74, 0.86, 0.96, 1.08],
      projectDrift: 4,
      budgetDrift: 1.1,
      igfDrift: 0.4,
    },
    yearly: {
      labels: YEARS,
      factors: [0.68, 0.79, 0.91, 1.04],
      projectDrift: 6,
      budgetDrift: 1.5,
      igfDrift: 0.55,
    },
  };
  const config = trendConfig[periodMeta.value];

  return config.labels.map((label, index) => {
    const drift = ((seed >> (index + 1)) % 11) - 5;
    const factor = config.factors[index];

    return {
      label,
      projects: Math.max(Math.round(projectsBase * factor + drift * config.projectDrift), 1),
      budget: round(clamp(budgetBase * factor + drift * config.budgetDrift, 18, 99)),
      igf: round(Math.max(igfBase * factor + drift * config.igfDrift, 2)),
    };
  });
}

function buildProjectRows(districtName, seed) {
  const districtToken = districtName.split(" ")[0];

  return PROJECT_BLUEPRINTS.map((template, index) => {
    const projectSeed = hashString(`${districtName}:${template.prefix}:${index}:${seed}`);
    const budget = (180 + (projectSeed % 620)) * 1000;
    const progress = clamp(28 + ((projectSeed >> 3) % 73), 22, 100);
    const spent = Math.min(
      budget,
      Math.round(budget * (progress / 100) * (0.78 + ((projectSeed >> 6) % 18) / 100)),
    );

    return {
      name: `${template.prefix} - ${districtToken}`,
      sector: template.sector,
      budget,
      spent,
      progress,
    };
  });
}

function buildSectorBreakdown(projectRows) {
  const totals = projectRows.reduce((acc, project) => {
    acc[project.sector] = (acc[project.sector] || 0) + project.budget;
    return acc;
  }, {});

  const grandTotal = Object.values(totals).reduce((sum, value) => sum + value, 0) || 1;

  return Object.entries(totals).map(([name, value]) => ({
    name,
    value: round((value / grandTotal) * 100),
    color: SECTOR_COLORS[name] || "hsl(215, 15%, 50%)",
  }));
}

function buildBudgetTimeline(seed, period, totalBudgetMillions, spentMillions, igfMillions) {
  const periodMeta = getPeriodMeta(period);

  if (periodMeta.value === "yearly") {
    const yearlyFactors = [0.76, 0.88, 0.97, 1.09];

    return YEARS.slice(-4).map((label, index) => {
      const wobble = ((seed >> (index + 2)) % 18) - 8;

      return {
        label,
        allocated: Math.max(Math.round(totalBudgetMillions * 1000 * yearlyFactors[index] + wobble * 18), 0),
        spent: Math.max(Math.round(spentMillions * 1000 * yearlyFactors[index] + wobble * 14), 0),
        igf: Math.max(Math.round(igfMillions * 1000 * yearlyFactors[index] + wobble * 7), 0),
      };
    });
  }

  const timelineConfig = {
    monthly: {
      labels: MONTHS,
      allocatedWeights: [0.14, 0.15, 0.16, 0.17, 0.18, 0.2],
      spentWeights: [0.11, 0.13, 0.16, 0.18, 0.2, 0.22],
      igfWeights: [0.1, 0.12, 0.15, 0.17, 0.21, 0.25],
      wobbleBase: 8,
    },
    quarterly: {
      labels: QUARTERS,
      allocatedWeights: [0.22, 0.24, 0.26, 0.28],
      spentWeights: [0.18, 0.24, 0.27, 0.31],
      igfWeights: [0.19, 0.23, 0.27, 0.31],
      wobbleBase: 10,
    },
  };
  const config = timelineConfig[periodMeta.value];

  return config.labels.map((label, index) => {
    const wobble = ((seed >> (index + 2)) % 16) - 7;

    return {
      label,
      allocated: Math.max(
        Math.round(totalBudgetMillions * 1000 * config.allocatedWeights[index] + wobble * config.wobbleBase),
        0,
      ),
      spent: Math.max(
        Math.round(spentMillions * 1000 * config.spentWeights[index] + wobble * (config.wobbleBase - 2)),
        0,
      ),
      igf: Math.max(
        Math.round(igfMillions * 1000 * config.igfWeights[index] + wobble * (config.wobbleBase - 5)),
        0,
      ),
    };
  });
}

function buildDistrictAlerts(districtName, regionName, projectRows, budgetUtilization, igfRate, meetings) {
  const slowestProject = [...projectRows].sort((a, b) => a.progress - b.progress)[0];

  return [
    {
      id: `${districtName}-critical`,
      level: "critical",
      message: `${slowestProject.name} is behind schedule`,
      location: `${districtName}, ${regionName}`,
    },
    {
      id: `${districtName}-warning`,
      level: "warning",
      message: `Budget utilization is at ${round(budgetUtilization)}%`,
      location: districtName,
    },
    {
      id: `${districtName}-info`,
      level: meetings < 8 ? "warning" : "info",
      message: meetings < 8 ? `Only ${meetings} meetings logged this year` : `IGF collection is at ${round(igfRate)}%`,
      location: districtName,
    },
  ];
}

// ==================== DHIS2 API Integration Functions ====================

function parseDashboardDateValue(value) {
  if (value == null || value === "" || value === "N/A") {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getProjectAttributeDate(instance, displayNames, attributeId) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, instance);
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matched = instance?.attributes?.find((attr) => attr.attribute === attributeId);
  return matched?.value || "";
}

/**
 * Count a project for selectedYear when that year is the start year,
 * the end year, or falls within the start–end period.
 */
function isProjectInSelectedYear(instance, selectedYear) {
  const year = parseInt(selectedYear, 10);
  if (!Number.isFinite(year)) {
    return false;
  }

  const startDateValue = getProjectAttributeDate(
    instance,
    ["Start Date", "Start date"],
    ATTRIBUTES.projectDate,
  );
  const endDateValue = getProjectAttributeDate(
    instance,
    ["End Date", "End date", "Expected Completion Date"],
    ATTRIBUTES.projectEndDate,
  );

  const startYear = getYearFromDateValue(startDateValue);
  const endYear = getYearFromDateValue(endDateValue);

  if (startYear === year || endYear === year) {
    return true;
  }

  const startDate = parseDashboardDateValue(startDateValue);
  const endDate = parseDashboardDateValue(endDateValue);
  const yearStart = new Date(`${year}-01-01T00:00:00`);
  const yearEnd = new Date(`${year}-12-31T23:59:59`);

  if (startDate && endDate) {
    return startDate <= yearEnd && endDate >= yearStart;
  }

  return false;
}

function mergeTrackedEntityInstances(...instanceGroups) {
  const byId = new Map();

  instanceGroups.flat().forEach((instance) => {
    const id = instance?.trackedEntity;
    if (id && !byId.has(id)) {
      byId.set(id, instance);
    }
  });

  return Array.from(byId.values());
}

async function fetchTrackedEntities(programId, locationCode, filterString) {
  const params = new URLSearchParams();
  params.set("orgUnit", locationCode);
  params.set("ouMode", "DESCENDANTS");
  params.set("program", programId);
  params.set("pageSize", "50000");
  params.set("fields", "trackedEntity,orgUnit,attributes[attribute,displayName,value],created");

  if (Array.isArray(filterString)) {
    filterString.filter(Boolean).forEach((filter) => params.append("filter", filter));
  } else if (filterString) {
    params.set("filter", filterString);
  }

  const result = await axios.get(`/tracker/trackedEntities?${params.toString()}`);
  return result?.data?.instances || [];
}

/**
 * Generic fetch function for tracked entities with filters
 * Uses ouMode=CHILDREN to fetch ALL descendants (region, district, community, etc.)
 */
async function fetchDashboardData(programId, locationCode, attributeFilters = {}, tracker) {
  try {
    let instances = [];

    if (tracker === "project" && attributeFilters.projectDate) {
      const selectedYear = attributeFilters.projectDate;
      const yearStart = `${selectedYear}-01-01`;
      const yearEnd = `${selectedYear}-12-31`;

      // Start date in selected year OR end date in selected year OR period spans the year
      const [byStartDate, byEndDate, bySpanningPeriod] = await Promise.all([
        fetchTrackedEntities(
          programId,
          locationCode,
          `${ATTRIBUTES.projectDate}:GE:${yearStart}:LE:${yearEnd}`,
        ),
        fetchTrackedEntities(
          programId,
          locationCode,
          `${ATTRIBUTES.projectEndDate}:GE:${yearStart}:LE:${yearEnd}`,
        ),
        fetchTrackedEntities(
          programId,
          locationCode,
          [
            `${ATTRIBUTES.projectDate}:LE:${yearEnd}`,
            `${ATTRIBUTES.projectEndDate}:GE:${yearStart}`,
          ],
        ),
      ]);

      instances = mergeTrackedEntityInstances(byStartDate, byEndDate, bySpanningPeriod)
        .filter((instance) => isProjectInSelectedYear(instance, selectedYear));
    } else {
      let filterString = "";

      if (attributeFilters.year) {
        filterString = `${ATTRIBUTES.year}:eq:${attributeFilters.year}`;
      } else if (attributeFilters.meetingDate && tracker === "meeting") {
        const startDate = `${attributeFilters.meetingDate}-01-01`;
        const endDate = `${attributeFilters.meetingDate}-12-31`;
        filterString = `${ATTRIBUTES.meetingDate}:GE:${startDate}:LE:${endDate}`;
      }

      instances = await fetchTrackedEntities(programId, locationCode, filterString);
    }

    const data = [];

    if (tracker === 'project') {

      instances.forEach(instance => {
        const tempData = {
          trackedEntity: instance.trackedEntity,
          orgUnit: instance.orgUnit,
          dd: getAttributeValue('Development Dimension', instance) || 'Unknown',
          sector: getAttributeValue('Sector', instance) || 'Unknown',
          type: getAttributeValue('Project & Programme Type', instance) || 'Unknown',
          contractSum: getAttributeValue('Contract Sum', instance) || 0,
          name: getAttributeValue('Name', instance) || '',
          description: getAttributeValue('Description', instance) || '',
          startDate: getProjectAttributeDate(
            instance,
            ["Start Date", "Start date"],
            ATTRIBUTES.projectDate,
          ),
          endDate: getProjectAttributeDate(
            instance,
            ["End Date", "End date", "Expected Completion Date"],
            ATTRIBUTES.projectEndDate,
          ),
        };

        data.push(tempData);
      });

    } else if (tracker === 'aap') {

      instances.forEach(instance => {
        const tempData = {
          trackedEntity: instance.trackedEntity,
          orgUnit: instance.orgUnit,
          dd: getAttributeValue('Development Dimension', instance) || 'Unknown',
        };

        data.push(tempData);
      });

    } else if (tracker === 'meeting') {

      instances.forEach(instance => {
        const tempData = {
          trackedEntity: instance.trackedEntity,
          orgUnit: instance.orgUnit,
          meetingType: getAttributeValue('DPAT | Meeting Type', instance) || 'Unknown',
          meetingDate: getAttributeValue('DPAT | Meeting Date', instance) || 'Unknown',
          hodFemaleParticipation: getAttributeValue('DPAT | Number of Participant - HoDs - Female', instance) || 0,
          hodMaleParticipation: getAttributeValue('DPAT | Number of Participant - HoDs - Male', instance) || 0,
          femaleAttendance: getAttributeValue('DPAT | Number of Attendance - Female', instance) || 0,
          maleAttendance: getAttributeValue('DPAT | Number of Attendance - Male', instance) || 0,
        };

        data.push(tempData);
      });

    } else if (tracker === 'igf') {

      instances.forEach(instance => {
        const tempData = {
          trackedEntity: instance.trackedEntity,
          orgUnit: instance.orgUnit,
          amountBudgeted: getIgfBudgetedFromInstance(instance),
          amount: getIgfCollectedFromInstance(instance),
          source: getIgfAttributeWithFallback(
            instance,
            ["IGF Source"],
            ATTRIBUTES.igfSource,
          ) || "Unknown",
        };

        data.push(tempData);
      });

    } else {

      console.error(`Unhandled tracker type: ${tracker}`);

    }


    // Group by orgUnit for efficient aggregation
    const groupedByOrgUnit = {};
    data.forEach(instance => {
      const orgUnit = instance.orgUnit;
      if (!groupedByOrgUnit[orgUnit]) {
        groupedByOrgUnit[orgUnit] = [];
      }
      groupedByOrgUnit[orgUnit].push(instance);
    });

    return { instances: data, groupedByOrgUnit };
  } catch (err) {
    console.error(`Error fetching dashboard data for program ${programId}:`, err);
    return { instances: [], groupedByOrgUnit: {} };
  }
}

// ==================== Data Fetching at Each Level ====================

/**
 * NATIONAL LEVEL - Fetches ALL data for the entire country (all orgUnit levels)
 * This includes regions, districts, communities, etc.
 */
async function fetchNationalData(year, orgUnitId = 'rHkDRHKXIdP') {

  const [aapData, projectData, meetingData, igfData] = await Promise.all([
    fetchDashboardData(PROGRAMS.annualActionPlan, orgUnitId, { year }, 'aap'),
    fetchDashboardData(PROGRAMS.project, orgUnitId, { projectDate: year }, 'project'),
    fetchDashboardData(PROGRAMS.meeting, orgUnitId, { meetingDate: year }, 'meeting'),
    fetchDashboardData(PROGRAMS.igf, orgUnitId, { year }, 'igf'),
  ]);


  const groupedData = projectData?.projectData?.groupedByOrgUnit
    || projectData?.groupedByOrgUnit;

  const flattenedData = Object.values(groupedData).flat();

  const projects = flattenedData.filter(
    item => item.type?.trim() === 'Project'
  );

  const programmes = flattenedData.filter(
    item => item.type?.trim() === 'Programme'
  );

  // Group projects and programmes by orgUnit for efficient aggregation
  const groupedProjectsByOrgUnit = {};
  projects.forEach(instance => {
    const orgUnit = instance.orgUnit;
    if (!groupedProjectsByOrgUnit[orgUnit]) {
      groupedProjectsByOrgUnit[orgUnit] = [];
    }
    groupedProjectsByOrgUnit[orgUnit].push(instance);
  });

  const groupedProgrammesByOrgUnit = {};
  programmes.forEach(instance => {
    const orgUnit = instance.orgUnit;
    if (!groupedProgrammesByOrgUnit[orgUnit]) {
      groupedProgrammesByOrgUnit[orgUnit] = [];
    }
    groupedProgrammesByOrgUnit[orgUnit].push(instance);
  });


  return {
    aap: aapData,
    projects: { projects, groupedByOrgUnit: groupedProjectsByOrgUnit },
    programmes: { programmes, groupedByOrgUnit: groupedProgrammesByOrgUnit },
    meetings: meetingData,
    igf: igfData,
  };
}

/**
 * REGIONAL LEVEL - Fetches ALL data within a specific region (including its districts and communities)
 */
async function fetchRegionalData(year, regionOrgUnitId) {

  const [aapData, projectData, meetingData, igfData] = await Promise.all([
    fetchDashboardData(PROGRAMS.annualActionPlan, regionOrgUnitId, { year }, 'aap'),
    fetchDashboardData(PROGRAMS.project, regionOrgUnitId, { projectDate: year }, 'project'),
    fetchDashboardData(PROGRAMS.meeting, regionOrgUnitId, { meetingDate: year }, 'meeting'),
    fetchDashboardData(PROGRAMS.igf, regionOrgUnitId, { year }, 'igf'),
  ]);


  const groupedData = projectData?.projectData?.groupedByOrgUnit
    || projectData?.groupedByOrgUnit;

  const flattenedData = Object.values(groupedData).flat();

  const projects = flattenedData.filter(
    item => item.type?.trim() === 'Project'
  );

  const programmes = flattenedData.filter(
    item => item.type?.trim() === 'Programme'
  );

  // Group projects and programmes by orgUnit for efficient aggregation
  const groupedProjectsByOrgUnit = {};
  projects.forEach(instance => {
    const orgUnit = instance.orgUnit;
    if (!groupedProjectsByOrgUnit[orgUnit]) {
      groupedProjectsByOrgUnit[orgUnit] = [];
    }
    groupedProjectsByOrgUnit[orgUnit].push(instance);
  });

  const groupedProgrammesByOrgUnit = {};
  programmes.forEach(instance => {
    const orgUnit = instance.orgUnit;
    if (!groupedProgrammesByOrgUnit[orgUnit]) {
      groupedProgrammesByOrgUnit[orgUnit] = [];
    }
    groupedProgrammesByOrgUnit[orgUnit].push(instance);
  });


  return {
    aap: aapData,
    projects: { projects, groupedByOrgUnit: groupedProjectsByOrgUnit },
    programmes: { programmes, groupedByOrgUnit: groupedProgrammesByOrgUnit },
    meetings: meetingData,
    igf: igfData,
  };
}


/**
 * DISTRICT LEVEL - Fetches ALL data for the selected district only (no children)
 */
async function fetchDistrictData(year, districtOrgUnitId) {

  const [aapData, projectData, meetingData, igfData] = await Promise.all([
    fetchDashboardData(PROGRAMS.annualActionPlan, districtOrgUnitId, { year }, 'aap'),
    fetchDashboardData(PROGRAMS.project, districtOrgUnitId, { projectDate: year }, 'project'),
    fetchDashboardData(PROGRAMS.meeting, districtOrgUnitId, { meetingDate: year }, 'meeting'),
    fetchDashboardData(PROGRAMS.igf, districtOrgUnitId, { year }, 'igf'),
  ]);

  return {
    aap: aapData,
    projects: projectData,
    meetings: meetingData,
    igf: igfData,
  };
}

/** Planned Activity Report status data element on the AAP program timeline. */
const AAP_PLANNED_ACTIVITY_STATUS_ELEMENT = "SZcHb5mvjJx";

/**
 * Count AAP plans whose Planned Activity Report timeline status is Completed.
 * District dashboard "Plans executed" uses this — not Projects & Programmes.
 */
async function countCompletedAapPlannedActivityReports(year, districtOrgUnitId, aapInstances) {
  if (!districtOrgUnitId || !Array.isArray(aapInstances) || aapInstances.length === 0) {
    return 0;
  }

  try {
    const eventsResp = await axios.get("/tracker/events", {
      params: {
        program: PROGRAMS.annualActionPlan,
        orgUnit: districtOrgUnitId,
        ouMode: "DESCENDANTS",
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
        pageSize: 10000,
        fields: "event,trackedEntity,status,dataValues[dataElement,value]",
      },
    });

    const reports = eventsResp?.data?.instances || eventsResp?.data?.events || [];
    const completedTeiIds = new Set();

    reports.forEach((report) => {
      const teiId = report?.trackedEntity;
      if (!teiId || completedTeiIds.has(teiId)) return;

      const hasCompletedStatus = (report?.dataValues || []).some(
        (value) =>
          value?.dataElement === AAP_PLANNED_ACTIVITY_STATUS_ELEMENT &&
          String(value?.value || "").trim() === "Completed",
      );

      if (hasCompletedStatus) {
        completedTeiIds.add(teiId);
      }
    });

    return aapInstances.filter((plan) => completedTeiIds.has(plan.trackedEntity)).length;
  } catch (error) {
    console.error("Failed to count completed AAP Planned Activity Reports:", error);
    return 0;
  }
}

// ==================== Helper Functions for District Dashboard ====================

/**
 * Calculate development dimensions from instances array
 */
function calculateDimensionsFromInstances(instances) {
  const dimensions = {};

  instances.forEach(instance => {
    const dd = instance.dd;
    if (dd && dd !== 'N/A' && dd !== 'Unknown') {
      dimensions[dd] = (dimensions[dd] || 0) + 1;
    } else if (!dd || dd === 'N/A') {
      dimensions['Uncategorized'] = (dimensions['Uncategorized'] || 0) + 1;
    }
  });

  // Sort by value descending
  const sortedDimensions = {};
  Object.entries(dimensions)
    .sort((a, b) => b[1] - a[1])
    .forEach(([key, value]) => {
      sortedDimensions[key] = value;
    });

  return sortedDimensions;
}

/**
 * Calculate projects by sector from instances
 */
function calculateProjectsBySector(instances) {
  const sectors = {};

  instances.forEach(instance => {
    const sector = instance.sector || 'Uncategorized';
    sectors[sector] = (sectors[sector] || 0) + 1;
  });

  const colorPalette = [
    'hsl(215, 60%, 22%)',
    'hsl(142, 70%, 45%)',
    'hsl(38, 92%, 50%)',
    'hsl(262, 52%, 55%)',
    'hsl(340, 82%, 52%)',
    'hsl(174, 60%, 40%)',
  ];

  return Object.entries(sectors)
    .map(([name, value], index) => ({
      name,
      value,
      color: SECTOR_COLORS[name] || colorPalette[index % colorPalette.length]
    }))
    .sort((a, b) => b.value - a.value);
}


/**
 * Build budget timeline from data
 */
function buildBudgetTimelineFromData(districtData, year) {
  // This would be populated from actual data
  // For now, return a default timeline
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const igfInstances = districtData.igf?.instances || [];

  // Group IGF by month (would need month from date attribute)
  const monthlyIGF = {};
  igfInstances.forEach(igf => {
    // You would extract month from igf.date here
    const month = "Jan"; // Placeholder
    monthlyIGF[month] = (monthlyIGF[month] || 0) + parseIgfAmount(igf.amount);
  });

  return months.map(month => ({
    label: month,
    allocated: 250000 + Math.random() * 50000,
    spent: 150000 + Math.random() * 40000,
    igf: monthlyIGF[month] || 10000 + Math.random() * 20000
  }));
}

/**
 * Build district alerts from actual data
 */
function buildDistrictAlertsFromData(districtName, regionName, projects, aaps, igfs, meetings) {
  const alerts = [];

  // Check for behind schedule projects
  const behindProjects = projects.filter(p => p.progress && p.progress < 30);
  if (behindProjects.length > 0) {
    alerts.push({
      id: `${districtName}-behind-projects`,
      level: "critical",
      message: `${behindProjects.length} project(s) are significantly behind schedule`,
      location: `${districtName}, ${regionName}`,
    });
  }

  // Check for low activity plan execution
  const executionRate = aaps.length > 0 ? (projects.length / aaps.length) * 100 : 0;
  if (executionRate < 50) {
    alerts.push({
      id: `${districtName}-low-execution`,
      level: "warning",
      message: `Activity plan execution is low (${Math.round(executionRate)}%)`,
      location: districtName,
    });
  }

  // Check for low IGF collection
  const totalIGF = sumIgfCollectedFromInstances(igfs);
  if (totalIGF < 500000) {
    alerts.push({
      id: `${districtName}-low-igf`,
      level: "info",
      message: `IGF collection is below target (GHS ${(totalIGF / 1000).toFixed(0)}k)`,
      location: districtName,
    });
  }

  // Check for low meeting count
  if (meetings.length < 6) {
    alerts.push({
      id: `${districtName}-few-meetings`,
      level: "warning",
      message: `Only ${meetings.length} meetings held this year, below target`,
      location: districtName,
    });
  }

  // Add positive alert if everything looks good
  if (alerts.length === 0) {
    alerts.push({
      id: `${districtName}-good-performance`,
      level: "info",
      message: `District is performing well across all metrics`,
      location: districtName,
    });
  }

  return alerts;
}

// ==================== Helper to get counts from grouped data ====================

function getAAPCount(data, orgUnitId) {
  return data.aap.groupedByOrgUnit[orgUnitId]?.length || 0;
}

function getAllAAPCount(data) {
  return Object.values(data.aap.groupedByOrgUnit).reduce((sum, arr) => sum + arr.length, 0);
}

function getProjectsCountByType(data, orgUnitId, projectType) {
  const instances = data.projects.groupedByOrgUnit[orgUnitId] || [];
  return instances.filter(instance => {
    const typeAttr = instance.attributes?.find(attr => attr.attribute === ATTRIBUTES.projectType);
    return typeAttr?.value === projectType;
  }).length;
}

function getTotalProjectsCount(data, orgUnitId) {
  const projects = getProjectsCountByType(data, orgUnitId, PROJECT_TYPES.project);
  const programmes = getProjectsCountByType(data, orgUnitId, PROJECT_TYPES.programme);
  return { projects, programmes, total: projects + programmes };
}

function getAllProjectsCount(data) {
  let projects = 0;
  let programmes = 0;

  Object.keys(data.projects.groupedByOrgUnit).forEach(orgUnitId => {
    const instances = data.projects.groupedByOrgUnit[orgUnitId] || [];
    instances.forEach(instance => {
      const typeAttr = instance.attributes?.find(attr => attr.attribute === ATTRIBUTES.projectType);
      if (typeAttr?.value === PROJECT_TYPES.project) {
        projects++;
      } else if (typeAttr?.value === PROJECT_TYPES.programme) {
        programmes++;
      }
    });
  });

  return { projects, programmes, total: projects + programmes };
}

function getMeetingsCount(data, orgUnitId) {
  return data.meetings.groupedByOrgUnit[orgUnitId]?.length || 0;
}

function getAllMeetingsCount(data) {
  return Object.values(data.meetings.groupedByOrgUnit).reduce((sum, arr) => sum + arr.length, 0);
}

function getIGFData(data, orgUnitId) {
  const instances = data.igf.groupedByOrgUnit[orgUnitId] || [];
  const totalAmount = sumIgfCollectedFromInstances(instances);
  return {
    count: instances.length,
    totalAmount,
    totalAmountMillions: round(totalAmount / 1000000, 2),
  };
}

// Original function (kept as is)
function getAllIGFData(data) {
  let totalAmount = 0;
  let totalCount = 0;

  Object.keys(data.igf?.groupedByOrgUnit || {}).forEach((orgUnitId) => {
    const instances = data.igf.groupedByOrgUnit[orgUnitId] || [];
    totalCount += instances.length;
    totalAmount += sumIgfCollectedFromInstances(instances);
  });

  return {
    count: totalCount,
    totalAmount,
    totalAmountMillions: round(totalAmount / 1000000, 2),
  };
}

// New function for region breakdown
function getIGFByRegion(data) {
  const districts = [];
  const storedDistricts = localStorage.getItem("districts");
  if (storedDistricts) {
    const parsedDistricts = JSON.parse(storedDistricts);
    districts.push(...parsedDistricts);
  }

  // Create a map of district ID to region name
  const districtToRegionMap = new Map(districts.map(d => [d.value.id, d.value.region]));

  // Create a map of region ID to region name (for direct region entries)
  const regionIdToNameMap = new Map(REGIONS.map(r => [r.id, r.displayName]));

  // Create a map of region name to region ID
  const regionNameToIdMap = new Map(REGIONS.map(r => [r.displayName, r.id]));

  // Calculate totals by region (start with all regions at 0)
  const regionTotalsMap = new Map();
  REGIONS.forEach(region => {
    regionTotalsMap.set(region.displayName, 0);
  });

  Object.entries(data || []).forEach(([orgUnitId, instances]) => {
    let regionName = null;

    // First, check if this ID is a district (maps to a region)
    if (districtToRegionMap.has(orgUnitId)) {
      regionName = districtToRegionMap.get(orgUnitId);
    }
    // If not a district, check if it's directly a region ID
    else if (regionIdToNameMap.has(orgUnitId)) {
      regionName = regionIdToNameMap.get(orgUnitId);
    }

    // Sum the amounts to the region
    if (regionName && regionTotalsMap.has(regionName)) {
      let totalAmountForOrgUnit = 0;

      // Calculate total amount for this org unit
      instances.forEach(instance => {
        if (instance.amount != null && instance.amount !== "N/A") {
          totalAmountForOrgUnit += parseIgfAmount(instance.amount);
        }
      });

      // Add to region total
      regionTotalsMap.set(
        regionName,
        regionTotalsMap.get(regionName) + totalAmountForOrgUnit
      );
    }
  });

  // Transform to the desired format
  const datagroupbyregion = Array.from(regionTotalsMap.entries()).map(([regionName, totalAmount]) => ({
    region: regionName,
    id: regionNameToIdMap.get(regionName),
    totalAmount: totalAmount,
    totalAmountMillions: round(totalAmount / 1000000, 2)
  }));

  return datagroupbyregion.sort((a, b) => b.totalAmount - a.totalAmount);
}


// ==================== Collect all orgUnits from the tree ====================

function collectAllOrgUnits(node, orgUnitsMap = {}) {
  if (node.id) {
    orgUnitsMap[node.id] = node;
  }
  if (node.children) {
    node.children.forEach(child => collectAllOrgUnits(child, orgUnitsMap));
  }
  return orgUnitsMap;
}

// ==================== Build Functions Using Fetched Data ====================

async function buildDistrictFromFetchedData(districtNode, regionName, year, nationalData) {
  const districtName = districtNode?.name || "District";
  const orgUnitId = districtNode?.id;
  const seed = hashString(`${regionName}:${districtName}:${year}`);
  const subdistrictCount = districtNode?.children?.length || 0;

  // Get counts from the pre-fetched data
  const activitiesCount = getAAPCount(nationalData, orgUnitId);
  const { projects: projectsCount, programmes: programmesCount, total: totalProjectsCount } = getTotalProjectsCount(nationalData, orgUnitId);
  const meetingsCount = getMeetingsCount(nationalData, orgUnitId);
  const igfData = getIGFData(nationalData, orgUnitId);

  // Use real data or fallback to generated data
  const projects = totalProjectsCount || (12 + (seed % 26) + subdistrictCount * 2);
  const budgetUtilization = activitiesCount > 0 ? clamp(activitiesCount * 5, 48, 92) : clamp(52 + (seed % 33), 48, 92);
  const igfRate = igfData.totalAmountMillions > 0 ? clamp(igfData.totalAmountMillions * 2, 28, 88) : clamp(34 + ((seed >> 2) % 45), 28, 88);
  const meetings = meetingsCount || (5 + (seed % 8) + Math.min(subdistrictCount, 3));

  const projectRows = buildProjectRows(districtName, seed);
  const sectorBreakdown = buildSectorBreakdown(projectRows);
  const projectBreakdown = buildProjectBreakdown(projects, budgetUtilization, igfRate, meetings, seed);
  const activityPlanBreakdown = buildActivityPlanBreakdown(
    projects,
    projectBreakdown.completedProjects,
    meetings,
    budgetUtilization,
    seed,
  );

  const budgetSpentMillions = igfData.totalAmountMillions > 0
    ? round(igfData.totalAmountMillions * 1.2, 2)
    : round(projectRows.reduce((sum, project) => sum + project.spent, 0) / 1000000, 2);

  const totalBudgetMillions = projectsCount > 0
    ? round(projectsCount * 0.45, 2)
    : round(projectRows.reduce((sum, project) => sum + project.budget, 0) / 1000000, 2);

  const igfMillions = igfData.totalAmountMillions || round(
    budgetSpentMillions * (0.19 + (igfRate / 100) * 0.22),
    2,
  );

  const budgetTimeline = buildBudgetTimeline(seed, "yearly", totalBudgetMillions, budgetSpentMillions, igfMillions);
  const riskScore = round(
    (100 - budgetUtilization) * 0.55 + Math.max(0, 55 - igfRate) * 0.45 + Math.max(0, 8 - meetings) * 4,
  );

  return {
    name: districtName,
    regionName,
    orgUnitId,
    projects,
    completedProjects: projectBreakdown.completedProjects,
    rolloverProjects: projectBreakdown.rolloverProjects,
    activityPlans: activityPlanBreakdown.activityPlans,
    plansExecuted: activityPlanBreakdown.plansExecuted,
    budget: round(budgetUtilization),
    igf: round(igfRate),
    meetings,
    budgetSpentMillions,
    totalBudgetMillions,
    igfMillions,
    trendData: buildTrendData(seed, "yearly", projects * 0.78, budgetUtilization * 0.64, igfMillions * 0.42),
    projectRows,
    sectorBreakdown,
    budgetTimeline,
    alerts: buildDistrictAlerts(districtName, regionName, projectRows, budgetUtilization, igfRate, meetings),
    riskScore,
    status: statusFromBudget(budgetUtilization),
  };
}

async function buildRegionFromFetchedData(regionNode, year, nationalData) {
  const regionName = regionNode?.name || "Region";

  // Build all districts using the pre-fetched national data
  const districtRows = await Promise.all(
    (regionNode?.children || []).map(async (districtNode) => {
      return await buildDistrictFromFetchedData(districtNode, regionName, year, nationalData);
    })
  );

  const districtCount = districtRows.length || 1;
  const totalProjects = districtRows.reduce((sum, district) => sum + district.projects, 0);
  const completedProjects = districtRows.reduce((sum, district) => sum + district.completedProjects, 0);
  const rolloverProjects = districtRows.reduce((sum, district) => sum + district.rolloverProjects, 0);
  const activityPlans = districtRows.reduce((sum, district) => sum + district.activityPlans, 0);
  const plansExecuted = districtRows.reduce((sum, district) => sum + district.plansExecuted, 0);
  const totalMeetings = districtRows.reduce((sum, district) => sum + district.meetings, 0);
  const totalIgfMillions = round(districtRows.reduce((sum, district) => sum + district.igfMillions, 0), 2);
  const averageBudget = average(districtRows.map((district) => district.budget));
  const averageIgf = average(districtRows.map((district) => district.igf));
  const reportingRate = clamp(62 + districtCount * 1.6 + (hashString(regionName) % 17), 66, 97);
  const passRate = clamp(54 + averageBudget * 0.34, 58, 93);

  return {
    name: regionName,
    projects: totalProjects,
    completedProjects,
    rolloverProjects,
    activityPlans,
    plansExecuted,
    budget: round(averageBudget),
    igf: round(averageIgf),
    meetings: totalMeetings,
    reportingRate: round(reportingRate),
    passRate: round(passRate),
    totalIgfMillions,
    districtRows,
    trendData: buildTrendData(
      hashString(regionName),
      "yearly",
      totalProjects * 0.84,
      averageBudget * 0.68,
      totalIgfMillions * 0.46,
    ),
    alerts: buildRegionalAlerts(regionName, districtRows),
    riskScore: round(average(districtRows.map((district) => district.riskScore))),
  };
}

function buildRegionalAlerts(regionName, districtRows) {
  const highestRiskDistricts = [...districtRows]
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 3);

  return highestRiskDistricts.map((district, index) => ({
    id: `${regionName}-${district.name}-${index}`,
    level: index === 0 ? "critical" : index === 1 ? "warning" : "info",
    message:
      index === 0
        ? `${district.name} needs immediate intervention`
        : index === 1
          ? `${district.name} is below target on budget and IGF`
          : `${district.name} is trending positively but still needs review`,
    location: `${district.name}, ${regionName}`,
  }));
}

function buildNationalAlerts(regionRows) {
  return [...regionRows]
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 5)
    .map((region, index) => ({
      id: `${region.name}-${index}`,
      level: index < 2 ? "critical" : index < 4 ? "warning" : "info",
      message:
        index < 2
          ? `${region.name} requires escalation`
          : index < 4
            ? `${region.name} is below the national benchmark`
            : `${region.name} is stabilizing after recent interventions`,
      location: `${region.name} Region`,
    }));
}

function buildNationalSectorBreakdown(regionRows) {
  const sectorTotals = regionRows.reduce((acc, region) => {
    region.districtRows.forEach((district) => {
      district.sectorBreakdown.forEach((sector) => {
        acc[sector.name] = (acc[sector.name] || 0) + sector.value;
      });
    });
    return acc;
  }, {});

  const total = Object.values(sectorTotals).reduce((sum, value) => sum + value, 0) || 1;

  return Object.entries(sectorTotals)
    .map(([name, value]) => ({
      name,
      value: round((value / total) * 100),
      color: SECTOR_COLORS[name] || "hsl(215, 15%, 50%)",
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
}

function resolveRegionNode(orgTree, currentLocation) {
  if (currentLocation?.level === "region") return currentLocation;

  if (currentLocation?.regionSlug) {
    return (orgTree?.children || []).find((region) => region.slug === currentLocation.regionSlug) || null;
  }

  if (currentLocation?.regionName) {
    return (orgTree?.children || []).find((region) => region.name === currentLocation.regionName) || null;
  }

  return orgTree?.children?.[0] || null;
}

function resolveDistrictNode(orgTree, currentLocation) {
  if (currentLocation?.level === "district") return currentLocation;

  const regionNode = resolveRegionNode(orgTree, currentLocation);
  if (!regionNode) return null;

  if (currentLocation?.districtSlug) {
    return (regionNode.children || []).find((district) => district.slug === currentLocation.districtSlug) || null;
  }

  if (currentLocation?.districtName) {
    return (regionNode.children || []).find((district) => district.name === currentLocation.districtName) || null;
  }

  return regionNode.children?.[0] || null;
}

// ==================== Mock Organization Tree (fallback if no context) ====================
const getMockOrgTree = () => ({
  name: "Ghana",
  id: "rHkDRHKXIdP",
  children: [
    {
      id: "R1",
      name: "Greater Accra",
      slug: "greater-accra",
      children: [
        { id: "D1", name: "Accra Metropolitan", slug: "accra-metropolitan", regionName: "Greater Accra", children: [] },
        { id: "D2", name: "Tema Metropolitan", slug: "tema-metropolitan", regionName: "Greater Accra", children: [] },
      ],
    },
    {
      id: "R2",
      name: "Ashanti",
      slug: "ashanti",
      children: [
        { id: "D3", name: "Kumasi Metropolitan", slug: "kumasi-metropolitan", regionName: "Ashanti", children: [] },
        { id: "D4", name: "Obuasi Municipal", slug: "obuasi-municipal", regionName: "Ashanti", children: [] },
      ],
    },
    {
      id: "R3",
      name: "Western",
      slug: "western",
      children: [
        { id: "D5", name: "Sekondi-Takoradi", slug: "sekondi-takoradi", regionName: "Western", children: [] },
        { id: "D6", name: "Wassa East", slug: "wassa-east", regionName: "Western", children: [] },
        { id: "D7", name: "Wassa Amenfi West", slug: "wassa-amenfi-west", regionName: "Western", children: [] },
      ],
    },
  ],
});

// ==================== Main Exported Functions ====================

/**
 * NATIONAL DASHBOARD - Makes only 4 API calls total (1 per program)
 * Fetches ALL data from all levels (regions, districts, communities)
 */

export async function getNationalDashboardData(orgTree = null, year) {

  const periodMeta = getPeriodMeta("yearly");
  const tree = orgTree || getMockOrgTree();

  // Fetch ALL data once at the national level (includes all descendants)
  const nationalData = await fetchNationalData(year);

  // Format data by region for each metric
  const aapgroupbyregion = formatData(nationalData?.aap?.groupedByOrgUnit);
  const projectgroupbyregion = formatData(nationalData?.projects?.groupedByOrgUnit);
  const programmegroupbyregion = formatData(nationalData?.programmes?.groupedByOrgUnit);
  const meetinggroupbyregion = formatData(nationalData?.meetings?.groupedByOrgUnit);
  const igfgroupbyregion = getIGFByRegion(nationalData?.igf?.groupedByOrgUnit);

  // Calculate development dimensions for Projects
  const projectsByDimension = calculateDimensions(nationalData?.projects?.groupedByOrgUnit);

  // Calculate development dimensions for Activity Plans (AAP)
  const aapByDimension = calculateDimensions(nationalData?.aap?.groupedByOrgUnit);

  // Calculate national totals
  const totalAAP = aapgroupbyregion.reduce((sum, region) => sum + region.total, 0);
  const totalProjects = projectgroupbyregion.reduce((sum, region) => sum + region.total, 0);
  const totalProgrammes = programmegroupbyregion.reduce((sum, region) => sum + region.total, 0);
  const totalMeetings = meetinggroupbyregion.reduce((sum, region) => sum + region.total, 0);
  const totalIgfAmount = igfgroupbyregion.reduce((sum, region) => sum + region.totalAmount, 0);

  // Calculate Activity Executed (Projects + Programmes)
  const totalActivityExecuted = totalProjects + totalProgrammes;
  const activityExecutionRate = totalAAP > 0 ? (totalActivityExecuted / totalAAP) * 100 : 0;

  // Create combined chart data (Plan vs Executed by region)
  const planVsExecutedData = aapgroupbyregion.map(region => {
    const projectsCount = projectgroupbyregion.find(p => p.region === region.region)?.total || 0;
    const programmesCount = programmegroupbyregion.find(p => p.region === region.region)?.total || 0;
    const executed = projectsCount + programmesCount;
    const plan = region.total;
    const executionRate = plan > 0 ? (executed / plan) * 100 : 0;

    return {
      region: region.region,
      id: region.id,
      plan: plan,
      executed: executed,
      executionRate: Math.round(executionRate)
    };
  }).sort((a, b) => b.plan - a.plan);

  // Dynamic colors for pie charts
  const colorPalette = [
    'hsl(215, 60%, 22%)',   // Dark blue
    'hsl(142, 70%, 45%)',   // Green
    'hsl(38, 92%, 50%)',    // Orange
    'hsl(262, 52%, 55%)',   // Purple
    'hsl(340, 82%, 52%)',   // Pink
    'hsl(174, 60%, 40%)',   // Teal
    'hsl(0, 72%, 51%)',     // Red
  ];

  // Define dimension abbreviations
  const dimensionAbbreviations = {
    'Economic Development (ED)': 'Econ Dev',
    'Social Development (SD)': 'Soc Dev',
    'Implementation, Coordination, Monitoring And Evaluation': 'ICME',
    'Governance, Corruption And Public Accountability(GCPA)': 'Gov & Account',
    'Environment, infrastructure and human settlement': 'Env & Infra',
    'Emergency Planning And Covid-19 Response': 'Emergency & Covid',
  };

  // Format pie chart data with abbreviations
  const projectsPieData = Object.entries(projectsByDimension).map(([name, value], index) => ({
    name: name,
    shortName: dimensionAbbreviations[name] || name.substring(0, 20) + '...',
    value,
    color: colorPalette[index % colorPalette.length]
  }));

  const aapPieData = Object.entries(aapByDimension).map(([name, value], index) => ({
    name: name,
    shortName: dimensionAbbreviations[name] || name.substring(0, 20) + '...',
    value,
    color: colorPalette[index % colorPalette.length]
  }));

  return {
    periodMeta,
    year,
    subtitle: `National development overview for ${year}`,
    metrics: {
      activityPlan: {
        value: formatCount(totalAAP),
        change: scaleChange(4.9, "yearly"),
        regionalData: aapgroupbyregion
      },
      projects: {
        value: formatInteger(totalProjects),
        change: scaleChange(7.4, "yearly"),
        regionalData: projectgroupbyregion
      },
      programmes: {
        value: formatCount(totalProgrammes),
        change: scaleChange(5.2, "yearly"),
        regionalData: programmegroupbyregion
      },
      activityExecuted: {
        value: formatCount(totalActivityExecuted),
        change: scaleChange(6.8, "yearly"),
        executionRate: Math.round(activityExecutionRate),
        subMetrics: [
          { label: "Projects", value: formatCount(totalProjects) },
          { label: "Programmes", value: formatCount(totalProgrammes) }
        ]
      },
      meetings: {
        value: formatInteger(totalMeetings),
        change: scaleChange(9.2, "yearly"),
        regionalData: meetinggroupbyregion
      },
      igfCollected: {
        value: formatIgfCurrency(totalIgfAmount),
        change: scaleChange(3.4, "yearly"),
        regionalData: igfgroupbyregion
      }
    },
    planVsExecutedData,
    projectsByDimension: projectsPieData,
    aapByDimension: aapPieData
  };
}

// Helper function to calculate development dimensions dynamically
function calculateDimensions(groupedData) {
  const dimensions = {};

  if (!groupedData) return dimensions;

  Object.values(groupedData).forEach(instances => {
    instances.forEach(instance => {
      const dd = instance.dd;
      if (dd && dd !== 'N/A') {
        dimensions[dd] = (dimensions[dd] || 0) + 1;
      } else {
        dimensions['N/A'] = (dimensions['N/A'] || 0) + 1;
      }
    });
  });

  // Sort dimensions by value descending
  const sortedDimensions = {};
  Object.entries(dimensions)
    .sort((a, b) => b[1] - a[1])
    .forEach(([key, value]) => {
      sortedDimensions[key] = value;
    });

  return sortedDimensions;
}


/**
 * REGIONAL DASHBOARD - Makes only 4 API calls total for the selected region
 * Fetches ALL data from the region, its districts, and communities
 */
/**
 * REGIONAL DASHBOARD - Makes only 4 API calls total for the selected region
 * Groups data by districts within the region (similar to how national groups by regions)
 */
export async function getRegionalDashboardData(orgTree, currentLocation, year) {
  const periodMeta = getPeriodMeta("yearly");
  const regionNode = resolveRegionNode(orgTree, currentLocation);

  if (!regionNode) {
    console.error("Region not found");
    return null;
  }

  // Fetch data once for this region (includes all districts and communities under it)
  const regionalData = await fetchRegionalData(year, currentLocation?.orgUnitId);

  // Format data by district for each metric
  const aapgroupbydistrict = formatRegionalData(regionalData?.aap?.groupedByOrgUnit);
  const projectgroupbydistrict = formatRegionalData(regionalData?.projects?.groupedByOrgUnit);
  const programmegroupbydistrict = formatRegionalData(regionalData?.programmes?.groupedByOrgUnit);
  const meetinggroupbydistrict = formatRegionalData(regionalData?.meetings?.groupedByOrgUnit);
  const igfgroupbydistrict = formatRegionalIGFData(regionalData?.igf?.groupedByOrgUnit);

  // Calculate development dimensions for Projects
  const projectsByDimension = calculateDimensions(regionalData?.projects?.groupedByOrgUnit);

  // Calculate development dimensions for Activity Plans (AAP)
  const aapByDimension = calculateDimensions(regionalData?.aap?.groupedByOrgUnit);

  // Calculate national totals
  const totalAAP = aapgroupbydistrict.reduce((sum, district) => sum + district.total, 0);
  const totalProjects = projectgroupbydistrict.reduce((sum, district) => sum + district.total, 0);
  const totalProgrammes = programmegroupbydistrict.reduce((sum, district) => sum + district.total, 0);
  const totalMeetings = meetinggroupbydistrict.reduce((sum, district) => sum + district.total, 0);
  const totalIgfAmount = igfgroupbydistrict.reduce((sum, district) => sum + district.totalAmount, 0);

  // Calculate Activity Executed (Projects + Programmes)
  const totalActivityExecuted = totalProjects + totalProgrammes;
  const activityExecutionRate = totalAAP > 0 ? (totalActivityExecuted / totalAAP) * 100 : 0;

  // Create combined chart data (Plan vs Executed by region)
  const planVsExecutedData = aapgroupbydistrict.map(district => {
    const projectsCount = projectgroupbydistrict.find(p => p.district === district.district)?.total || 0;
    const programmesCount = programmegroupbydistrict.find(p => p.district === district.district)?.total || 0;
    const executed = projectsCount + programmesCount;
    const plan = district.total;
    const executionRate = plan > 0 ? (executed / plan) * 100 : 0;

    return {
      district: district.district,
      id: district.id,
      plan: plan,
      executed: executed,
      executionRate: Math.round(executionRate)
    };
  }).sort((a, b) => b.plan - a.plan);

  // Dynamic colors for pie charts
  const colorPalette = [
    'hsl(215, 60%, 22%)',   // Dark blue
    'hsl(142, 70%, 45%)',   // Green
    'hsl(38, 92%, 50%)',    // Orange
    'hsl(262, 52%, 55%)',   // Purple
    'hsl(340, 82%, 52%)',   // Pink
    'hsl(174, 60%, 40%)',   // Teal
    'hsl(0, 72%, 51%)',     // Red
  ];

  // Define dimension abbreviations
  const dimensionAbbreviations = {
    'Economic Development (ED)': 'Econ Dev',
    'Social Development (SD)': 'Soc Dev',
    'Implementation, Coordination, Monitoring And Evaluation': 'ICME',
    'Governance, Corruption And Public Accountability(GCPA)': 'Gov & Account',
    'Environment, infrastructure and human settlement': 'Env & Infra',
    'Emergency Planning And Covid-19 Response': 'Emergency & Covid',
  };

  // Format pie chart data with abbreviations
  const projectsPieData = Object.entries(projectsByDimension).map(([name, value], index) => ({
    name: name,
    shortName: dimensionAbbreviations[name] || name.substring(0, 20) + '...',
    value,
    color: colorPalette[index % colorPalette.length]
  }));

  const aapPieData = Object.entries(aapByDimension).map(([name, value], index) => ({
    name: name,
    shortName: dimensionAbbreviations[name] || name.substring(0, 20) + '...',
    value,
    color: colorPalette[index % colorPalette.length]
  }));

  const districtRows = (regionNode.children || []).map((district) => {
    const districtAAP = aapgroupbydistrict.find((d) => d.id === district.orgUnitId)?.total || 0;
    const districtProjects = projectgroupbydistrict.find((d) => d.id === district.orgUnitId)?.total || 0;
    const districtProgrammes = programmegroupbydistrict.find((d) => d.id === district.orgUnitId)?.total || 0;
    const districtMeetings = meetinggroupbydistrict.find((d) => d.id === district.orgUnitId)?.total || 0;
    const districtIGF = igfgroupbydistrict.find((d) => d.id === district.orgUnitId)?.totalAmount || 0;
    const executed = districtProjects + districtProgrammes;

    return {
      name: district.name,
      id: district.orgUnitId || district.id,
      projects: districtProjects,
      programmes: districtProgrammes,
      totalActivities: executed,
      activityPlans: districtAAP,
      meetings: districtMeetings,
      igfCollected: districtIGF,
      planExecutionPercentage: Math.round(planExecutionPercentage(districtAAP, executed)),
      status: statusFromBudget(districtAAP, executed),
    };
  }).sort((a, b) => {
    const rateDiff = b.planExecutionPercentage - a.planExecutionPercentage;
    if (rateDiff !== 0) return rateDiff;
    return a.name.localeCompare(b.name);
  });

  return {
    periodMeta,
    regionName: regionNode.name,
    year,
    subtitle: `${regionNode.name} - Development overview for ${year}`,
    metrics: {
      activityPlan: {
        value: formatCount(totalAAP),
        change: scaleChange(4.9, "yearly"),
        districtData: aapgroupbydistrict
      },
      projects: {
        value: formatInteger(totalProjects),
        change: scaleChange(7.4, "yearly"),
        districtData: projectgroupbydistrict
      },
      programmes: {
        value: formatCount(totalProgrammes),
        change: scaleChange(5.2, "yearly"),
        districtData: programmegroupbydistrict
      },
      activityExecuted: {
        value: formatCount(totalActivityExecuted),
        change: scaleChange(6.8, "yearly"),
        executionRate: Math.round(activityExecutionRate),
        subMetrics: [
          { label: "Projects", value: formatCount(totalProjects) },
          { label: "Programmes", value: formatCount(totalProgrammes) }
        ]
      },
      meetings: {
        value: formatInteger(totalMeetings),
        change: scaleChange(9.2, "yearly"),
        districtData: meetinggroupbydistrict
      },
      igfCollected: {
        value: formatIgfCurrency(totalIgfAmount),
        change: scaleChange(3.4, "yearly"),
        districtData: igfgroupbydistrict
      }
    },
    planVsExecutedData,
    projectsByDimension: projectsPieData,
    aapByDimension: aapPieData,
    // Additional regional specific data
    totalDistricts: regionNode.children?.length || 0,
    districtRows,
    alerts: buildRegionalAlertsFromData(regionNode.name, districtRows, {
      activityExecutionRate,
      totalAAP,
      totalActivityExecuted,
    }),
  };
}

// ==================== Helper Functions for Regional Dashboard ====================

/**
 * DISTRICT DASHBOARD - Makes only 4 API calls total for the selected district
 * Fetches ALL data from the selected district only
 */

export async function getDistrictDashboardData(orgTree, currentLocation, year) {

  const periodMeta = getPeriodMeta("yearly");
  const districtNode = resolveDistrictNode(orgTree, currentLocation);
  const regionName = districtNode?.regionName || currentLocation?.regionName || "Region";

  if (!districtNode) {
    console.error("District not found");
    return null;
  }

  // Fetch data for this district only
  const districtData = await fetchDistrictData(year, districtNode.orgUnitId);

  // Get instances from fetched data
  const aapInstances = districtData.aap?.instances || [];
  const projectInstances = districtData.projects?.instances || [];
  const meetingInstances = districtData.meetings?.instances || [];
  const igfInstances = districtData.igf?.instances || [];

  // Separate projects and programmes
  const projects = projectInstances.filter(p => p.type?.trim() === 'Project');
  const programmes = projectInstances.filter(p => p.type?.trim() === 'Programme');

  // Calculate development dimensions for Projects
  const projectsByDimension = calculateDimensionsFromInstances(projects);

  // Calculate development dimensions for Activity Plans (AAP)
  const aapByDimension = calculateDimensionsFromInstances(aapInstances);

  // Calculate projects by sector
  const projectsBySector = calculateProjectsBySector(projects);

  // Calculate meetings by type
  const meetingsByType = calculateMeetingsByType(meetingInstances);

  // Calculate meeting participation
  const meetingParticipation = calculateMeetingParticipation(meetingInstances);

  // Calculate IGF by source (budgeted vs collected)
  const igfBySource = calculateIGFBySourceDetailed(igfInstances);

  // Calculate totals
  const totalAAP = aapInstances.length;
  const totalProjects = projects.length;
  const totalProgrammes = programmes.length;
  const totalMeetings = meetingInstances.length;

  // IGF calculations
  const totalIgfBudgeted = igfInstances.reduce(
    (sum, igf) => sum + parseIgfAmount(igf.amountBudgeted),
    0,
  );
  const totalIgfCollected = sumIgfCollectedFromInstances(igfInstances);

  // Plans executed = AAP plans with Planned Activity Report status Completed
  const plansExecuted = await countCompletedAapPlannedActivityReports(
    year,
    districtNode.orgUnitId,
    aapInstances,
  );
  const activityExecutionRate = totalAAP > 0 ? (plansExecuted / totalAAP) * 100 : 0;

  // Build project rows from actual project instances
  const projectRows = buildProjectRowsFromInstances(projects);

  // Build sector breakdown from actual data
  const sectorBreakdown = buildSectorBreakdownFromProjects(projects);


  // Build alerts from actual data
  const alerts = buildDistrictAlertsFromData(
    districtNode.name,
    regionName,
    projects,
    aapInstances,
    igfInstances,
    meetingInstances,
    totalIgfBudgeted,
    totalIgfCollected
  );

  return {
    periodMeta,
    districtName: districtNode.name,
    regionName: regionName,
    year,
    metrics: {
      activityPlan: {
        value: formatCount(totalAAP),
        change: scaleChange(3.8, "yearly"),
        subMetrics: [{ label: "Plans executed", value: formatCount(plansExecuted) }],
      },
      activeProjects: {
        value: formatInteger(totalProjects),
        change: scaleChange(5.1, "yearly"),
      },
      programmes: {
        value: formatCount(totalProgrammes),
        change: scaleChange(4.2, "yearly"),
      },
      igfBudgeted: {
        value: formatIgfCurrency(totalIgfBudgeted),
        change: scaleChange(3.2, "yearly"),
      },
      igfCollected: {
        value: formatIgfCurrency(totalIgfCollected),
        change: scaleChange(3.6, "yearly"),
        subMetrics: [
          { label: "Collection Rate", value: `${totalIgfBudgeted > 0 ? Math.round((totalIgfCollected / totalIgfBudgeted) * 100) : 0}%` }
        ]
      },
      meetingsHeld: {
        value: formatInteger(totalMeetings),
        change: scaleChange(2.9, "yearly")
      },
    },
    projectRows,
    sectorBreakdown,
    projectsByDimension,
    aapByDimension,
    meetingsByType,
    meetingParticipation,
    igfBySource,
    alerts,
    summaryStats: {
      totalProjects,
      totalProgrammes,
      totalAAP,
      totalMeetings,
      totalIgfBudgeted,
      totalIgfCollected,
      totalIgfMillions: round(totalIgfCollected / 1000000, 2),
      activityExecutionRate: Math.round(activityExecutionRate),
      plansExecuted
    }
  };
}


/**
 * Build budget timeline from IGF data
 */
function buildBudgetTimelineFromIGFData(igfInstances, year) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Initialize monthly data
  const monthlyData = {};
  months.forEach(month => {
    monthlyData[month] = { allocated: 0, spent: 0, igf: 0 };
  });

  // If you have dates in your IGF instances, you can group by month
  // For now, distribute IGF amounts evenly or based on meeting dates if available
  const totalIGF = sumIgfCollectedFromInstances(igfInstances);
  const monthlyIGF = totalIGF / 12;

  months.forEach((month, index) => {
    const progress = (index + 1) / 12;
    monthlyData[month] = {
      label: month,
      allocated: round(5000000 * (0.3 + progress * 0.7), 0),
      spent: round(3000000 * progress, 0),
      igf: round(monthlyIGF, 0)
    };
  });

  return Object.values(monthlyData);
}



/**
 * Calculate meetings by type from instances
 */
function calculateMeetingsByType(instances) {
  const meetingTypes = {};

  instances.forEach(instance => {
    const type = instance.meetingType || 'Unknown';
    meetingTypes[type] = (meetingTypes[type] || 0) + 1;
  });

  const MEETING_TYPE_COLORS = {
    "DPAT Meeting": "hsl(215, 60%, 22%)",
    "Technical Review": "hsl(142, 70%, 45%)",
    "Stakeholder Forum": "hsl(38, 92%, 50%)",
    "Planning Session": "hsl(262, 52%, 55%)",
    "Review Meeting": "hsl(340, 82%, 52%)",
    "Training Workshop": "hsl(174, 60%, 40%)",
  };

  const colorPalette = [
    'hsl(215, 60%, 22%)',
    'hsl(142, 70%, 45%)',
    'hsl(38, 92%, 50%)',
    'hsl(262, 52%, 55%)',
    'hsl(340, 82%, 52%)',
    'hsl(174, 60%, 40%)',
  ];

  return Object.entries(meetingTypes)
    .map(([name, value], index) => ({
      name,
      value,
      color: MEETING_TYPE_COLORS[name] || colorPalette[index % colorPalette.length]
    }))
    .sort((a, b) => b.value - a.value);
}

// Helper function for IGF by source with budgeted vs collected
function calculateIGFBySourceDetailed(instances) {
  const sources = new Map();

  instances.forEach(instance => {
    const source = instance.source || 'Other';
    if (!sources.has(source)) {
      sources.set(source, { budgeted: 0, collected: 0 });
    }
    const data = sources.get(source);
    data.budgeted += parseIgfAmount(instance.amountBudgeted);
    data.collected += parseIgfAmount(instance.amount);
  });

  return Array.from(sources.entries())
    .map(([name, { budgeted, collected }]) => ({
      name,
      budgeted,
      collected,
      percentage: budgeted > 0 ? (collected / budgeted) * 100 : 0
    }))
    .sort((a, b) => b.budgeted - a.budgeted);
}

// Helper function for meeting participation
function calculateMeetingParticipation(instances) {
  const participation = {
    hod: { male: 0, female: 0 },
    attendance: { male: 0, female: 0 }
  };

  instances.forEach(instance => {
    participation.hod.male += parseInt(instance.hodMaleParticipation) || 0;
    participation.hod.female += parseInt(instance.hodFemaleParticipation) || 0;
    participation.attendance.male += parseInt(instance.maleAttendance) || 0;
    participation.attendance.female += parseInt(instance.femaleAttendance) || 0;
  });

  return [
    { type: 'HoDs', male: participation.hod.male, female: participation.hod.female },
    { type: 'General Attendance', male: participation.attendance.male, female: participation.attendance.female }
  ];
}


// Updated buildProjectRowsFromInstances
function buildProjectRowsFromInstances(instances) {
  if (instances.length === 0) return [];

  return instances.map((instance, index) => ({
    name: instance.name || `Project ${index + 1}`,
    sector: instance.sector || 'Not Specified',
    dd: instance.dd || 'Not Specified',
    contractSum: parseFloat(instance.contractSum) || 0,
    trackedEntity: instance.trackedEntity
  }));
}

/**
 * Build sector breakdown from projects
 */
function buildSectorBreakdownFromProjects(instances) {
  if (instances.length === 0) {
    return [];
  }

  return calculateProjectsBySector(instances);
}

/**
 * Build regional alerts from district performance data
 */
function buildRegionalAlertsFromData(regionName, districtRows, summary = {}) {
  if (!districtRows?.length) {
    return [{
      id: `${regionName}-no-data`,
      level: "info",
      category: "Data Availability",
      title: "No district records found",
      message: "Performance alerts cannot be generated until district reporting data is available.",
      location: `${regionName} Region`,
    }];
  }

  const alerts = [];
  const totalDistricts = districtRows.length;
  const avgExecution = round(average(districtRows.map((district) => district.planExecutionPercentage)));
  const atRiskCount = districtRows.filter((district) => district.status === "Low" || district.status === "At Risk").length;
  const goodCount = districtRows.filter((district) => district.status === "Good").length;
  const onTrackCount = districtRows.filter((district) => district.status === "On Track").length;
  const regionalExecutionRate = summary.activityExecutionRate != null
    ? Math.round(summary.activityExecutionRate)
    : avgExecution;

  alerts.push({
    id: `${regionName}-summary`,
    level: regionalExecutionRate < 50 ? "critical" : regionalExecutionRate < 70 ? "warning" : "info",
    category: "Regional Overview",
    title: `${regionName} region execution rate: ${regionalExecutionRate}%`,
    message: `${goodCount} Good, ${onTrackCount} On Track, ${atRiskCount} need intervention across ${totalDistricts} districts. ${summary.totalActivityExecuted ?? 0} of ${summary.totalAAP ?? 0} planned activities executed region-wide.`,
    location: `${regionName} Region`,
    metric: `${regionalExecutionRate}% regional execution`,
  });

  const executionIssues = [...districtRows]
    .filter((district) => district.activityPlans > 0 && district.planExecutionPercentage < 70)
    .sort((a, b) => a.planExecutionPercentage - b.planExecutionPercentage);

  executionIssues.slice(0, 6).forEach((district) => {
    const executed = district.projects + district.programmes;
    const level = district.planExecutionPercentage < 30
      ? "critical"
      : district.planExecutionPercentage < 50
        ? "warning"
        : "info";

    alerts.push({
      id: `${regionName}-${district.id || district.name}-execution`,
      level,
      category: "Activity Execution",
      title: `${district.name}: ${district.status} performance`,
      message: executed === 0
        ? `${district.activityPlans} activities planned but none reported as executed this period.`
        : `Executed ${executed} of ${district.activityPlans} planned activities (${district.planExecutionPercentage}% execution rate).`,
      location: district.name,
      metric: `${district.planExecutionPercentage}%`,
      recommendation: district.planExecutionPercentage < 50
        ? "Prioritize completion of pending activities and review underperforming projects."
        : "Monitor progress to maintain momentum toward Good status.",
    });
  });

  const avgIgf = average(districtRows.map((district) => district.igfCollected || 0));
  if (avgIgf > 0) {
    districtRows
      .filter((district) => (district.igfCollected || 0) < avgIgf * 0.4)
      .sort((a, b) => (a.igfCollected || 0) - (b.igfCollected || 0))
      .slice(0, 3)
      .forEach((district) => {
        alerts.push({
          id: `${regionName}-${district.id || district.name}-igf`,
          level: (district.igfCollected || 0) === 0 ? "critical" : "warning",
          category: "IGF Collection",
          title: `${district.name}: Below-average revenue collection`,
          message: `Collected ${formatIgfCurrency(district.igfCollected || 0)} IGF compared with the regional average of ${formatIgfCurrency(avgIgf)}.`,
          location: district.name,
          metric: formatIgfCurrency(district.igfCollected || 0),
          recommendation: "Review revenue mobilization strategies and follow up on outstanding collections.",
        });
      });
  }

  const meetingTarget = 6;
  districtRows
    .filter((district) => (district.meetings || 0) < meetingTarget)
    .sort((a, b) => (a.meetings || 0) - (b.meetings || 0))
    .slice(0, 3)
    .forEach((district) => {
      alerts.push({
        id: `${regionName}-${district.id || district.name}-meetings`,
        level: (district.meetings || 0) < 3 ? "warning" : "info",
        category: "Governance",
        title: `${district.name}: Insufficient coordination meetings`,
        message: `Only ${district.meetings || 0} meetings held against a target of ${meetingTarget} for the reporting period.`,
        location: district.name,
        metric: `${district.meetings || 0} meetings`,
        recommendation: "Schedule regular coordination and review meetings to improve oversight.",
      });
    });

  districtRows
    .filter((district) => district.planExecutionPercentage >= 70 && district.activityPlans > 0)
    .slice(0, 2)
    .forEach((district, index) => {
      alerts.push({
        id: `${regionName}-${district.id || district.name}-highlight`,
        level: "info",
        category: "Top Performer",
        title: `#${index + 1} ${district.name}: ${district.status} execution`,
        message: `${district.projects + district.programmes} activities executed from ${district.activityPlans} planned (${district.planExecutionPercentage}%).`,
        location: district.name,
        metric: `${district.planExecutionPercentage}%`,
      });
    });

  const levelOrder = { critical: 0, warning: 1, info: 2 };
  const categoryOrder = {
    "Regional Overview": 0,
    "Activity Execution": 1,
    "IGF Collection": 2,
    Governance: 3,
    "Top Performer": 4,
  };

  return [...new Map(alerts.map((alert) => [alert.id, alert])).values()].sort((a, b) => {
    const levelDiff = levelOrder[a.level] - levelOrder[b.level];
    if (levelDiff !== 0) return levelDiff;
    return (categoryOrder[a.category] ?? 99) - (categoryOrder[b.category] ?? 99);
  });
}




// Export years array for the dropdown
export { YEARS };